import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../api/client.js'

interface SetupStep {
  key: string
  done: boolean
  count: number
}

const STEPS: Record<string, { title: string; hint: string; to?: string }> = {
  property: {
    title: 'Kunde und Liegenschaft anlegen',
    hint: 'Wem das Haus gehört und wo es steht, oder gleich per Excel importieren.',
    to: '/objekte',
  },
  unit: {
    title: 'Gebäude und Einheiten anlegen',
    hint: 'Was darunter liegt, Haus 3, Wohnung 3.02, Treppenhaus, Müllstandplatz.',
    to: '/objekte',
  },
  template: {
    title: 'Leistung beschreiben',
    hint: 'Was regelmäßig zu tun ist, samt Fotopflicht.',
    to: '/vorlagen',
  },
  staff: { title: 'Mitarbeiter anlegen', hint: 'Wer die Arbeit ausführt.', to: '/mitarbeiter' },
  plan: {
    title: 'Aufgabenplan erstellen',
    hint: 'Leistung + Objekt + Rhythmus. Ab hier entstehen die Aufgaben von selbst.',
    to: '/plaene',
  },
  device: {
    title: 'Mitarbeiter einladen',
    hint: 'Er öffnet den Link auf dem Handy und gibt den Code ein.',
    to: '/mitarbeiter',
  },
  completed: {
    title: 'Erste Aufgabe erledigen',
    hint: 'Passiert auf dem Handy des Mitarbeiters, danach steht sie im Nachweis.',
  },
}

/**
 * A fresh installation is empty everywhere, and every screen then shows the same
 * "nothing here yet". This is the point at which a pilot customer gives up, so
 * the dashboard leads through the first ten minutes and then disappears for good.
 */
export function SetupChecklist() {
  const { data } = useQuery({
    queryKey: ['setup'],
    queryFn: () => api<{ steps: SetupStep[]; complete: boolean }>('/api/setup'),
  })

  if (!data || data.complete) return null

  const doneCount = data.steps.filter((step) => step.done).length
  const nextKey = data.steps.find((step) => !step.done)?.key

  return (
    <section className="card setup" style={{ marginBottom: 22 }}>
      <div className="card-head">
        <h2>Erste Schritte</h2>
        <span className="muted small">
          {doneCount} von {data.steps.length} erledigt
        </span>
      </div>
      <div className="card-body">
        <ol className="setup-list">
          {data.steps.map((step) => {
            const meta = STEPS[step.key]
            if (!meta) return null
            const isNext = step.key === nextKey
            return (
              <li key={step.key} className={`${step.done ? 'done' : ''} ${isNext ? 'next' : ''}`}>
                <span className="mark" aria-hidden="true">
                  {step.done ? '✓' : ''}
                </span>
                <div className="text">
                  <div className="t">
                    {meta.title}
                    {step.done && step.count > 0 && <span className="muted small">, {step.count}</span>}
                  </div>
                  {isNext && <div className="muted small">{meta.hint}</div>}
                </div>
                {isNext && meta.to && (
                  <Link className="btn small" to={meta.to}>
                    Los
                  </Link>
                )}
              </li>
            )
          })}
        </ol>
      </div>
    </section>
  )
}
